import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type AppleSyncResult =
  | { ok: true; active: boolean; expiresAt: string | null; plan: string }
  | { ok: false; error: string; reason?: string };

const MESSAGES: Record<string, string> = {
  other_account:
    "This Apple subscription is already linked to a different Recovery Trauma™ account. Please sign in with that account.",
  website_member:
    "You already have a membership bought on the Recovery Trauma™ website, so there's nothing more to buy.",
  unknown_product: "We didn't recognise that plan.",
};

/**
 * Called by the iPhone app after a purchase or when restoring purchases.
 * Accepts only transaction ids; the facts come from Apple.
 */
export const syncApplePurchases = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ transactionIds: z.array(z.string().regex(/^\d{1,40}$/)).min(1).max(20) }).parse(d),
  )
  .handler(async ({ data, context }): Promise<AppleSyncResult> => {
    const { fetchAppleTransaction, fetchLatestForOriginal, applyAppleTransaction } = await import(
      "./apple-iap.server"
    );
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = (context.claims as { email?: string })?.email ?? null;

    let best: AppleSyncResult | null = null;
    const seen = new Set<string>();
    try {
      for (const id of data.transactionIds) {
        const { tx: first } = await fetchAppleTransaction(id);
        if (seen.has(first.originalTransactionId)) continue;
        seen.add(first.originalTransactionId);

        if (first.appAccountToken && first.appAccountToken.toLowerCase() !== context.userId.toLowerCase()) {
          best ??= { ok: false, error: MESSAGES.other_account, reason: "other_account" };
          continue;
        }
        const latest = (await fetchLatestForOriginal(first.originalTransactionId))?.tx ?? first;
        const r = await applyAppleTransaction(supabaseAdmin, context.userId, email, latest);
        if (!r.ok) {
          best ??= { ok: false, error: MESSAGES[r.reason], reason: r.reason };
          continue;
        }
        if (!best || !best.ok || (r.active && !best.active)) best = r;
      }
    } catch (e) {
      console.error("[apple-iap] sync failed", e);
      return { ok: false, error: e instanceof Error ? e.message : "Apple check failed" };
    }
    return best ?? { ok: false, error: "No Apple purchases found for this Apple ID." };
  });

/**
 * Records the member's express digital-content consent immediately before the
 * Apple purchase sheet opens, matching the website checkout.
 */
export const recordAppleDigitalConsent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        productId: z.string().min(1).max(200),
        accepted: z.literal(true),
        version: z.string().min(1).max(50),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { DIGITAL_CONSENT_VERSION } = await import("./digital-consent");
    if (data.version !== DIGITAL_CONSENT_VERSION) {
      return { ok: false as const, error: "Please review the updated consent and tick the box again." };
    }
    const { recordDigitalConsent } = await import("./digital-consent.server");
    await recordDigitalConsent({
      userId: context.userId,
      email: (context.claims as { email?: string })?.email ?? null,
      planId: data.productId,
      provider: "apple",
    });
    return { ok: true as const };
  });

/** Whether this account may buy through Apple (no live website membership). */
export const getAppleEligibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("memberships")
      .select("status, billing_provider, stripe_subscription_id, apple_expires_at")
      .eq("user_id", context.userId)
      .maybeSingle();
    const { data: member } = await context.supabase.rpc("is_founding_member", {
      _user_id: context.userId,
    });
    const provider = (data?.billing_provider ?? "").toLowerCase();
    return {
      isMember: !!member,
      provider: provider || null,
      appleExpiresAt: data?.apple_expires_at ?? null,
    };
  });
